/**
 * CommentsPanel — sidebar tab listing the active document's comments.
 *
 * Comments are created by clicking "Add" here and then picking a position on
 * the canvas (see `CommentOverlay.tsx` for the pins, placement catcher and
 * draft composer). Each thread supports replies, inline editing, resolving
 * (which hides its pin) and deletion. "Locate" scrolls the viewport to the
 * comment's pin.
 */

import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import {
  checkIcon,
  pencilIcon,
  PlusIcon,
  TrashIcon,
} from "@excalidraw/excalidraw/components/icons";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { useAtom } from "../app-jotai";

import {
  addReply,
  commentsAtom,
  commentPlacementAtom,
  deleteComment,
  deleteReply,
  editComment,
  highlightedCommentIdAtom,
  toggleCommentResolved,
} from "./CommentManager";

import "./CommentsPanel.scss";

import type { CanvasComment } from "./commentStorage";

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const formatTimestamp = (timestamp: number): string => {
  const deltaMs = Date.now() - timestamp;
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return new Date(timestamp).toLocaleDateString();
};

const crosshairIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="7" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </svg>
);

// ---------------------------------------------------------------------------
// message composer (new reply / inline edit) — shared input behavior
// ---------------------------------------------------------------------------

const CommentInput = ({
  initialValue = "",
  placeholder,
  submitLabel,
  autoFocus = false,
  onSubmit,
  onCancel,
}: {
  initialValue?: string;
  placeholder: string;
  submitLabel: string;
  autoFocus?: boolean;
  onSubmit: (value: string) => void;
  onCancel?: () => void;
}) => {
  const [value, setValue] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const submit = () => {
    if (value.trim()) {
      onSubmit(value);
      setValue("");
    }
  };

  return (
    <div className="comments-panel__input">
      <textarea
        ref={textareaRef}
        value={value}
        placeholder={placeholder}
        rows={2}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          // keep global canvas shortcuts from firing while typing
          event.stopPropagation();
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            submit();
          } else if (event.key === "Escape" && onCancel) {
            event.preventDefault();
            onCancel();
          }
        }}
      />
      <div className="comments-panel__input-actions">
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="comments-panel__submit"
          disabled={!value.trim()}
          onClick={submit}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// single comment thread
// ---------------------------------------------------------------------------

const CommentCard = ({ comment }: { comment: CanvasComment }) => {
  const excalidrawAPI = useExcalidrawAPI();
  const [highlightedId, setHighlightedId] = useAtom(highlightedCommentIdAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const isHighlighted = highlightedId === comment.id;
  const isResolved = comment.resolvedAt != null;

  // scroll the card into view when it's highlighted from a canvas pin click
  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [isHighlighted]);

  const locate = () => {
    setHighlightedId(comment.id);
    excalidrawAPI?.setViewport({
      target: { x: comment.x, y: comment.y, width: 1, height: 1 },
      fit: "scale-down",
      animation: true,
    });
  };

  return (
    <div
      ref={cardRef}
      className={clsx("comments-panel__comment", {
        "comments-panel__comment--resolved": isResolved,
        "comments-panel__comment--highlighted": isHighlighted,
      })}
      data-comment-id={comment.id}
    >
      <div className="comments-panel__comment-header">
        <span className="comments-panel__avatar" aria-hidden="true">
          {comment.author.charAt(0).toUpperCase()}
        </span>
        <span className="comments-panel__author" title={comment.author}>
          {comment.author}
        </span>
        <span className="comments-panel__timestamp">
          {formatTimestamp(comment.createdAt)}
        </span>
        {isResolved && (
          <span className="comments-panel__resolved-badge">Resolved</span>
        )}
      </div>

      {isEditing ? (
        <CommentInput
          initialValue={comment.message}
          placeholder="Edit comment"
          submitLabel="Save"
          autoFocus
          onSubmit={(value) => {
            editComment(comment.id, value);
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <div className="comments-panel__message">{comment.message}</div>
      )}

      {comment.replies.length > 0 && (
        <div className="comments-panel__replies">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="comments-panel__reply">
              <div className="comments-panel__comment-header">
                <span className="comments-panel__avatar" aria-hidden="true">
                  {reply.author.charAt(0).toUpperCase()}
                </span>
                <span className="comments-panel__author" title={reply.author}>
                  {reply.author}
                </span>
                <span className="comments-panel__timestamp">
                  {formatTimestamp(reply.createdAt)}
                </span>
                <span className="comments-panel__actions comments-panel__actions--inline">
                  <button
                    type="button"
                    title="Delete reply"
                    onClick={() => deleteReply(comment.id, reply.id)}
                  >
                    {TrashIcon}
                  </button>
                </span>
              </div>
              <div className="comments-panel__message">{reply.message}</div>
            </div>
          ))}
        </div>
      )}

      {isReplying && (
        <CommentInput
          placeholder="Reply…"
          submitLabel="Reply"
          autoFocus
          onSubmit={(value) => {
            addReply(comment.id, value);
            setIsReplying(false);
          }}
          onCancel={() => setIsReplying(false)}
        />
      )}

      <div className="comments-panel__comment-footer">
        <button
          type="button"
          className="comments-panel__text-button"
          onClick={() => setIsReplying((value) => !value)}
        >
          Reply
        </button>
        <span className="comments-panel__actions">
          <button type="button" title="Show on canvas" onClick={locate}>
            {crosshairIcon}
          </button>
          {!isEditing && (
            <button
              type="button"
              title="Edit"
              onClick={() => setIsEditing(true)}
            >
              {pencilIcon}
            </button>
          )}
          <button
            type="button"
            title={isResolved ? "Reopen" : "Resolve"}
            onClick={() => toggleCommentResolved(comment.id)}
          >
            {checkIcon}
          </button>
          <button
            type="button"
            title="Delete"
            onClick={() => deleteComment(comment.id)}
          >
            {TrashIcon}
          </button>
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// panel
// ---------------------------------------------------------------------------

export const CommentsPanel = () => {
  const [comments] = useAtom(commentsAtom);
  const [placementActive, setPlacementActive] = useAtom(commentPlacementAtom);
  const [showResolved, setShowResolved] = useState(false);

  const openComments = comments
    .filter((comment) => comment.resolvedAt == null)
    .sort((a, b) => a.createdAt - b.createdAt);
  const resolvedComments = comments
    .filter((comment) => comment.resolvedAt != null)
    .sort((a, b) => a.createdAt - b.createdAt);

  return (
    <div className="comments-panel">
      <div className="comments-panel__header">
        <span className="comments-panel__title">
          Comments
          {openComments.length > 0 && (
            <span className="comments-panel__count">{openComments.length}</span>
          )}
        </span>
        <button
          type="button"
          className={clsx("comments-panel__new-button", {
            "comments-panel__new-button--active": placementActive,
          })}
          title="Add a comment on the canvas"
          onClick={() => setPlacementActive(!placementActive)}
        >
          {PlusIcon}
          <span>{placementActive ? "Cancel" : "Add"}</span>
        </button>
      </div>

      {placementActive && (
        <div className="comments-panel__notice">
          Click anywhere on the canvas to place the comment. Press Escape to
          cancel.
        </div>
      )}

      <div className="comments-panel__list">
        {openComments.length === 0 && resolvedComments.length === 0 && (
          <div className="comments-panel__empty">
            No comments yet. Click “Add” and pick a spot on the canvas to start
            a thread.
          </div>
        )}
        {openComments.length === 0 && resolvedComments.length > 0 && (
          <div className="comments-panel__empty">No open comments.</div>
        )}

        {openComments.map((comment) => (
          <CommentCard key={comment.id} comment={comment} />
        ))}

        {resolvedComments.length > 0 && (
          <button
            type="button"
            className="comments-panel__text-button comments-panel__toggle-resolved"
            onClick={() => setShowResolved((value) => !value)}
          >
            {showResolved ? "Hide" : "Show"} resolved ({resolvedComments.length}
            )
          </button>
        )}

        {showResolved &&
          resolvedComments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
      </div>
    </div>
  );
};
